import {
  Box,
  ButtonBase,
  Chip,
  Paper,
  styled,
  Typography
} from '@mui/material';

import type { ElementType } from 'react';

import {
  AMBER,
  AMBER_DARK,
  AMBER_LIGHT,
  GRAY,
  GRAY_LIGHT_TERTIARY,
  GRAY_SECONDARY,
  MINT,
  NEW_RED_LIGHT,
  PRIMARY,
  PRIMARY_LIGHT,
  RED_MUTED,
  RED_MUTED_LIGHT
} from 'constants/colors';

import type { RuleSetCardSize, RuleSetCardStatus } from './RuleSetCard.types';

const STATUS_BAR_COLORS: Record<RuleSetCardStatus, string> = {
  active: PRIMARY,
  concept: AMBER,
  expired: GRAY
};

const STATUS_CHIP_COLORS: Record<
  RuleSetCardStatus,
  { borderColor: string; color: string; backgroundColor: string }
> = {
  active: { borderColor: MINT, color: PRIMARY, backgroundColor: PRIMARY_LIGHT },
  concept: {
    borderColor: AMBER,
    color: AMBER_DARK,
    backgroundColor: AMBER_LIGHT
  },
  expired: {
    borderColor: GRAY,
    color: GRAY_SECONDARY,
    backgroundColor: GRAY_LIGHT_TERTIARY
  }
};

export const Card = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2, 2.5),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
}));

export const Header = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'clickable'
})<{ clickable: boolean }>(({ theme, clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  cursor: clickable ? 'pointer' : 'default'
}));

export const StatusBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: RuleSetCardStatus }>(({ theme, status }) => ({
  width: 4,
  flexShrink: 0,
  alignSelf: 'stretch',
  borderRadius: 9999,
  backgroundColor: STATUS_BAR_COLORS[status],
  margin: theme.spacing(0, 1)
}));

export const HeaderContent = styled(Box)({
  flex: 1,
  minWidth: 0
});

const sizeProp = { shouldForwardProp: (prop: PropertyKey) => prop !== 'size' };

/** A `large` card's title line holds taller buttons, so it needs less space below it */
export const TopRow = styled(
  Box,
  sizeProp
)<{ size: RuleSetCardSize }>(({ theme, size }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(size === 'large' ? 0.5 : 1.5)
}));

/** Sizes an `Icon` with `fontSize="inherit"`; width and height are pinned so the SVG's own attributes cannot win */
const IconBox = styled('span')({
  display: 'flex',
  flexShrink: 0,
  '& svg': { width: '1em', height: '1em' }
});

export const TitleIcon = styled(
  IconBox,
  sizeProp
)<{ size: RuleSetCardSize }>(({ theme, size }) => ({
  fontSize: theme.typography.pxToRem(size === 'large' ? 22 : 20),
  color: theme.palette.primary.main
}));

export const MetaIcon = styled(
  IconBox,
  sizeProp
)<{ size: RuleSetCardSize }>(({ theme, size }) => ({
  fontSize: theme.typography.pxToRem(size === 'large' ? 16 : 14)
}));

/** A `large` card sits a step above the list size, not a full heading size */
export const Title = styled(
  Typography,
  sizeProp
)<{ size: RuleSetCardSize; component?: ElementType }>(({ theme, size }) => ({
  fontWeight: 600,
  ...(size === 'large' && { fontSize: theme.typography.pxToRem(20) })
}));

export const MetaText = styled(
  Typography,
  sizeProp
)<{ size: RuleSetCardSize; component?: ElementType }>(({ theme, size }) => ({
  ...(size === 'large' && { fontSize: theme.typography.pxToRem(15) })
}));

const chipBase = {
  height: 24,
  fontSize: '12px',
  fontWeight: 500
};

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: RuleSetCardStatus }>(({ status }) => ({
  ...chipBase,
  marginLeft: 4,
  ...STATUS_CHIP_COLORS[status]
}));

export const UrgencyChip = styled(Chip)(({ theme }) => ({
  ...chipBase,
  borderColor: NEW_RED_LIGHT,
  color: RED_MUTED,
  backgroundColor: RED_MUTED_LIGHT,
  '& .MuiChip-icon': {
    fontSize: 15,
    color: RED_MUTED,
    marginLeft: theme.spacing(1)
  }
}));

/** Pushed to the far right of the title line */
export const Actions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: 'auto'
}));

export const MetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  color: theme.palette.text.secondary
}));

export const MetaItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}));

export const ChevronButton = styled(ButtonBase)(({ theme }) => ({
  flexShrink: 0,
  alignSelf: 'center',
  display: 'flex',
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(24),
  borderRadius: theme.shape.borderRadius,
  '& svg': { width: '1em', height: '1em' }
}));

export const Chevron = styled('span', {
  shouldForwardProp: (prop) => prop !== 'expanded'
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  display: 'flex',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', { duration: 200 })
}));

export const Body = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginInline: theme.spacing(-2.5),
  paddingInline: theme.spacing(2.5),
  paddingTop: theme.spacing(2),
  borderTop: `2px solid ${theme.palette.divider}`
}));
